import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Table, Text, Button, Loader } from '@mantine/core';
import { IconCreditCard } from '@tabler/icons-react';
import styled from 'styled-components';
import { getBillingData } from '../../../store/reducers/propertyManager';

const MOCK_BILLING = {
    billingInfo: {
        currentTier: 'Premium',
        nextBillDate: 'Oct 1, 2024',
        estimatedAmount: 14250,
    },
    paymentMethodSetup: false,
    paymentHistory: [
        { id: 1, date: '03/1/2024', invoiceId: '20003001', description: 'Monthly Subscription', amount: 14250, status: 'Paid' },
        { id: 2, date: '03/1/2024', invoiceId: '20003004', description: 'Monthly Subscription', amount: 14250, status: 'Pending' },
        { id: 3, date: '04/1/2024', invoiceId: '20003004', description: 'Monthly Subscription', amount: 17.50, status: 'Pending' },
        { id: 4, date: '04/1/2024', invoiceId: '20003028', description: 'Monthly Subscription', amount: 18.50, status: 'Pending' },
        { id: 5, date: '04/1/2024', invoiceId: '20003028', description: 'Monthly Subscription', amount: 18.50, status: 'Pending' },
    ],
};

const STATUS_COLORS = {
    Paid: 'green',
    Pending: 'yellow',
    Failed: 'red',
};

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const BillingPayments = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);

    const reduxBillingInfo = useSelector((s) => s.propertyManager.billingInfo);
    const reduxPaymentHistory = useSelector((s) => s.propertyManager.paymentHistory);
    const reduxPaymentMethodSetup = useSelector((s) => s.propertyManager.paymentMethodSetup);

    const billingInfo =
        reduxBillingInfo?.currentTier ? reduxBillingInfo : MOCK_BILLING.billingInfo;
    const paymentHistory = reduxPaymentHistory?.length
        ? reduxPaymentHistory
        : MOCK_BILLING.paymentHistory;
    const paymentMethodSetup =
        reduxPaymentMethodSetup !== undefined ? reduxPaymentMethodSetup : MOCK_BILLING.paymentMethodSetup;

    useEffect(() => {
        setIsLoading(true);
        dispatch(getBillingData({ setLoading: setIsLoading }));
    }, [dispatch]);

    const handleStripeSetup = () => {
        // Stripe setup handler — to be wired to backend payment session
    };

    return (
        <PageWrapper>
            <PageHeader>
                <Text fw={800} fz={28} tt="uppercase" c="#1a1a2e">
                    Billing &amp; Payments
                </Text>
            </PageHeader>

            {isLoading && (
                <LoaderWrapper>
                    <Loader color="var(--blackbox-blue)" size="sm" />
                </LoaderWrapper>
            )}

            <SummaryRow>
                <BillingCard>
                    <CardTitle>Monthly Billing Summary</CardTitle>
                    <BillingRow>
                        <BillingKey>Current Tier:</BillingKey>
                        <BillingValue>{billingInfo.currentTier}</BillingValue>
                    </BillingRow>
                    <BillingRow>
                        <BillingKey>Next Bill Date:</BillingKey>
                        <BillingValue>{billingInfo.nextBillDate}</BillingValue>
                    </BillingRow>
                    <BillingRow>
                        <BillingKey>Estimated Amount:</BillingKey>
                        <BillingValue>{formatCurrency(billingInfo.estimatedAmount)}</BillingValue>
                    </BillingRow>
                </BillingCard>

                <PaymentMethodCard>
                    <PaymentMethodHeader>
                        <div>
                            <CardTitle>Payment Method:</CardTitle>
                            <PaymentMethodStatus $setup={paymentMethodSetup}>
                                {paymentMethodSetup ? 'Connected' : 'Setup Required'}
                            </PaymentMethodStatus>
                        </div>
                        <IconCreditCard size={32} color="#adb5bd" stroke={1.2} />
                    </PaymentMethodHeader>

                    {!paymentMethodSetup && (
                        <Button
                            color="green"
                            fullWidth
                            mt="md"
                            radius="md"
                            onClick={handleStripeSetup}
                        >
                            Set Up Payment via Stripe
                        </Button>
                    )}
                    {paymentMethodSetup && (
                        <Text fz="sm" c="teal" mt="sm" fw={600}>
                            Payment method connected.
                        </Text>
                    )}
                </PaymentMethodCard>
            </SummaryRow>

            <SectionTitle>Payment History</SectionTitle>

            <TableWrapper>
                <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Date</Table.Th>
                            <Table.Th>Invoice ID</Table.Th>
                            <Table.Th>Description</Table.Th>
                            <Table.Th>Amount</Table.Th>
                            <Table.Th>Status</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {paymentHistory.length ? (
                            paymentHistory.map((item) => (
                                <Table.Tr key={item.id}>
                                    <Table.Td>{item.date}</Table.Td>
                                    <Table.Td>{item.invoiceId}</Table.Td>
                                    <Table.Td>{item.description}</Table.Td>
                                    <Table.Td>{formatCurrency(item.amount)}</Table.Td>
                                    <Table.Td>
                                        <Badge
                                            color={STATUS_COLORS[item.status] || 'gray'}
                                            variant="light"
                                            size="sm"
                                        >
                                            {item.status}
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            ))
                        ) : (
                            <Table.Tr>
                                <Table.Td colSpan={5}>
                                    <Text c="dimmed" ta="center" fz="sm" py="md">
                                        No payment history found.
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                    </Table.Tbody>
                </Table>
            </TableWrapper>
        </PageWrapper>
    );
};

export default BillingPayments;

const PageWrapper = styled.div`
    padding: 28px 32px;
    height: 100%;
    overflow-y: auto;
`;

const PageHeader = styled.div`
    margin-bottom: 24px;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;
`;

const SummaryRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 32px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const BillingCard = styled.div`
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
`;

const PaymentMethodCard = styled.div`
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
`;

const PaymentMethodHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const CardTitle = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 12px 0;
`;

const PaymentMethodStatus = styled.p`
    font-size: 16px;
    font-weight: 700;
    color: ${({ $setup }) => ($setup ? '#2e7d32' : '#c0392b')};
    margin: 0;
`;

const BillingRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #f1f3f5;

    &:last-child {
        border-bottom: none;
    }
`;

const BillingKey = styled.span`
    font-size: 13px;
    color: #495057;
`;

const BillingValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
`;

const SectionTitle = styled.h2`
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #1a1a2e;
    margin: 0 0 12px 0;
`;

const TableWrapper = styled.div`
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;
`;
